import { useContext, useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { AddOutlined, CloseOutlined, DeleteOutline } from '@mui/icons-material';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { IVisibilityCondition, ISurveyQuestionGroupItem } from '../../../shared/services/survey/survey.model';

const OP_LABELS: Record<IVisibilityCondition['op'], string> = {
  answered: 'is answered',
  equals: '=',
  notEquals: '≠',
  in: 'is one of',
  gt: '>',
  lt: '<',
  gte: '≥',
  lte: '≤',
};
const ALL_OPS = Object.keys(OP_LABELS) as IVisibilityCondition['op'][];
const ANSWERED_ANY = '__any__';

interface Props {
  api?: { updateGroupItem: (groupId: string, itemId: string, payload: any) => Promise<any> };
  open: boolean;
  onClose: () => void;
  successHandler: () => void;
  groupId: string;
  item: ISurveyQuestionGroupItem;
  precedingItems: ISurveyQuestionGroupItem[];
}

export const GroupItemVisibilityDialog = ({ open, onClose, successHandler, groupId, item, precedingItems , api}: Props) => {
  const userNotificationsContext = useContext(UserNotificationsContext);
  const surveyAdminService = SurveyAdminService.getInstance();

  const [conditions, setConditions] = useState<IVisibilityCondition[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setConditions(item.visibilityRule?.allOf ? [...item.visibilityRule.allOf] : []);
      setIsSubmitting(false);
    }
  }, [open, item]);

  const choiceOptions = (questionId: string) =>
    precedingItems.find(i => i.surveyQuestionId === questionId)?.surveyQuestion?.surveyQuestionOptions ?? [];

  const addCondition = () => {
    const firstId = precedingItems[0]?.surveyQuestionId ?? '';
    setConditions(prev => [...prev, { questionId: firstId, op: 'answered', value: undefined }]);
  };

  const update = (idx: number, patch: Partial<IVisibilityCondition>) =>
    setConditions(prev => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  const remove = (idx: number) =>
    setConditions(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const sanitized: IVisibilityCondition[] = conditions.map(c => {
        if (c.op === 'answered') {
          const opts = choiceOptions(c.questionId);
          if (opts.length > 0 && typeof c.value === 'string' && c.value && c.value !== ANSWERED_ANY) {
            return { questionId: c.questionId, op: 'equals' as const, value: c.value };
          }
          return { questionId: c.questionId, op: 'answered' as const };
        }
        if (c.op === 'in') {
          const arr = Array.isArray(c.value)
            ? c.value
            : typeof c.value === 'string'
              ? c.value.split(',').map(s => s.trim()).filter(Boolean)
              : [];
          return { questionId: c.questionId, op: 'in' as const, value: arr };
        }
        if (c.op === 'gt' || c.op === 'lt' || c.op === 'gte' || c.op === 'lte') {
          const n = typeof c.value === 'number' ? c.value : Number(c.value);
          return { questionId: c.questionId, op: c.op, value: Number.isFinite(n) ? n : 0 };
        }
        return { questionId: c.questionId, op: c.op, value: c.value as string };
      });

      const visibilityRule = sanitized.length > 0 ? { allOf: sanitized } : null;
      await (api?.updateGroupItem ?? ((g: string, i: string, pl: any) => surveyAdminService.updateGroupItem(g, i, pl)))(groupId, item.id, { visibilityRule } as any);
      userNotificationsContext.openSnackbar({ open: true, message: 'Display conditions saved', backgroundColor: 'primary.dark' });
      successHandler();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to save conditions', backgroundColor: 'text.primary' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = async () => {
    setIsSubmitting(true);
    try {
      await (api?.updateGroupItem ?? ((g: string, i: string, pl: any) => surveyAdminService.updateGroupItem(g, i, pl)))(groupId, item.id, { visibilityRule: null } as any);
      userNotificationsContext.openSnackbar({ open: true, message: 'Display conditions removed', backgroundColor: 'primary.dark' });
      successHandler();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to remove conditions', backgroundColor: 'text.primary' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ pr: 5 }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Box>
            <Typography variant='h6' component='span' fontWeight={600}>Display conditions</Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25 }}>
              {item.surveyQuestion?.prompt ?? 'Question'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={isSubmitting} sx={{ mr: -0.75 }}>
            <CloseOutlined />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          All conditions below must be met for this question to be shown (logical AND). Leave empty to always show this question.
        </Typography>

        {precedingItems.length === 0 && (
          <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
            No preceding questions available: this is the first question in the group.
          </Typography>
        )}

        {precedingItems.length > 0 && (
          <Stack spacing={1.5}>
            {conditions.length === 0 && (
              <Typography variant='body2' color='text.secondary'>
                No conditions set: this question is always shown.
              </Typography>
            )}

            {conditions.map((c, idx) => {
              const opts = choiceOptions(c.questionId);
              const isChoice = opts.length > 0;
              const showValue = c.op !== 'answered';

              return (
                <Stack key={idx} direction='row' spacing={1} alignItems='flex-start' flexWrap='wrap' sx={{ rowGap: 1 }}>
                  {/* Question selector */}
                  <FormControl size='small' sx={{ minWidth: 200, flex: 1 }}>
                    <InputLabel>Question</InputLabel>
                    <Select
                      label='Question'
                      value={c.questionId}
                      onChange={e => update(idx, { questionId: e.target.value, value: undefined })}
                    >
                      {precedingItems.map(pi => (
                        <MenuItem key={pi.surveyQuestionId} value={pi.surveyQuestionId}>
                          {pi.surveyQuestion?.prompt ?? pi.surveyQuestionId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Operator */}
                  <FormControl size='small' sx={{ width: 130 }}>
                    <InputLabel>Condition</InputLabel>
                    <Select
                      label='Condition'
                      value={c.op}
                      onChange={e => update(idx, { op: e.target.value as IVisibilityCondition['op'], value: undefined })}
                    >
                      {ALL_OPS.map(op => (
                        <MenuItem key={op} value={op}>{OP_LABELS[op]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* "answered" with choice question — allow selecting a specific option or any */}
                  {c.op === 'answered' && isChoice && (
                    <FormControl size='small' sx={{ minWidth: 200, flex: 1 }}>
                      <InputLabel>Which answer?</InputLabel>
                      <Select
                        label='Which answer?'
                        value={typeof c.value === 'string' && c.value && c.value !== ANSWERED_ANY ? c.value : ANSWERED_ANY}
                        onChange={e => update(idx, { value: e.target.value === ANSWERED_ANY ? undefined : e.target.value })}
                      >
                        <MenuItem value={ANSWERED_ANY}>Any selected option</MenuItem>
                        {opts.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}

                  {/* "answered" with free-text question */}
                  {c.op === 'answered' && !isChoice && (
                    <Typography variant='body2' color='text.secondary' sx={{ alignSelf: 'center', flex: 1 }}>
                      Any answer counts
                    </Typography>
                  )}

                  {/* equals / notEquals with choice options */}
                  {showValue && isChoice && (c.op === 'equals' || c.op === 'notEquals') && (
                    <FormControl size='small' sx={{ minWidth: 200, flex: 1 }}>
                      <InputLabel>Option</InputLabel>
                      <Select
                        label='Option'
                        value={typeof c.value === 'string' ? c.value : ''}
                        onChange={e => update(idx, { value: e.target.value })}
                      >
                        {opts.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}

                  {/* "in" with choice options — multi-select */}
                  {showValue && isChoice && c.op === 'in' && (
                    <FormControl size='small' sx={{ minWidth: 200, flex: 1 }}>
                      <InputLabel>Options (match any)</InputLabel>
                      <Select
                        label='Options (match any)'
                        multiple
                        value={Array.isArray(c.value) ? c.value : []}
                        onChange={e => update(idx, { value: e.target.value as string[] })}
                        renderValue={sel => (sel as string[]).map(id => opts.find(o => o.id === id)?.label ?? id).join(', ')}
                      >
                        {opts.map(o => (
                          <MenuItem key={o.id} value={o.id}>
                            <Checkbox checked={Array.isArray(c.value) && c.value.includes(o.id)} />
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Numeric / free-text value */}
                  {showValue && (!isChoice || (c.op !== 'equals' && c.op !== 'notEquals' && c.op !== 'in')) && (
                    <TextField
                      size='small'
                      label={c.op === 'in' ? 'Values (comma-separated)' : 'Value'}
                      value={
                        c.op === 'in' && !isChoice
                          ? (Array.isArray(c.value) ? c.value.join(', ') : (c.value as string) ?? '')
                          : (c.value as string | number) ?? ''
                      }
                      onChange={e => {
                        if (c.op === 'in') {
                          update(idx, { value: e.target.value });
                        } else if (c.op === 'gt' || c.op === 'lt' || c.op === 'gte' || c.op === 'lte') {
                          update(idx, { value: e.target.value === '' ? '' as any : Number(e.target.value) });
                        } else {
                          update(idx, { value: e.target.value });
                        }
                      }}
                      type={c.op === 'gt' || c.op === 'lt' || c.op === 'gte' || c.op === 'lte' ? 'number' : 'text'}
                      sx={{ minWidth: 150, flex: 1 }}
                    />
                  )}

                  <IconButton size='small' onClick={() => remove(idx)} aria-label='Remove condition' disabled={isSubmitting}>
                    <DeleteOutline fontSize='small' />
                  </IconButton>
                </Stack>
              );
            })}

            <Box>
              <Button size='small' startIcon={<AddOutlined />} onClick={addCondition} disabled={isSubmitting}>
                Add condition
              </Button>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={handleClear}
          disabled={isSubmitting || (!item.visibilityRule && conditions.length === 0)}
          color='error'
          variant='text'
        >
          Clear all conditions
        </Button>
        <Stack direction='row' spacing={1}>
          <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={isSubmitting || precedingItems.length === 0}
          >
            Save
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
